import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Textarea } from '@rudi/ui/textarea';
import { toast } from 'sonner';
import {
  useApproveAccessRequest,
  useInvestigatorAccessRequests,
  useRejectAccessRequest,
} from '../api/hooks';
import type { InvestigatorAccessRequest } from '../api/types';

const CREATE_TEAM = '__create__';

function relationLabel(request: InvestigatorAccessRequest): string {
  return request.relation === 'principal_investigator'
    ? 'Says they are the PI'
    : "Works in the PI's team";
}

function errorText(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { error?: string; detail?: string } } })
    ?.response?.data;
  return response?.error ?? response?.detail ?? (error instanceof Error ? error.message : fallback);
}

/**
 * A member's requests to work for a catalog PI: where the PI's team is, and
 * Approve (place the member there) or Reject. Hidden on older Rudi releases.
 */
export function AccessRequestCard({ userId }: { userId: string }) {
  const requests = useInvestigatorAccessRequests(userId);
  if (!requests.data?.length) return null;
  const pending = requests.data.filter(request => request.status === 'pending');
  const decided = requests.data.filter(
    request => request.status === 'approved' || request.status === 'rejected'
  );
  return (
    <section
      aria-label="PI access requests"
      className="mb-5 space-y-3 rounded-3xl border bg-card p-4"
    >
      <h2 className="text-base font-semibold">PI access requests</h2>
      {pending.map(request => (
        <PendingRequest key={request.id} request={request} />
      ))}
      {decided.length > 0 && (
        <ul className="space-y-1 text-sm text-muted-foreground">
          {decided.map(request => (
            <li key={request.id}>
              {request.pi_name ?? request.pi_slug} · {relationLabel(request)} ·{' '}
              {request.status === 'approved' ? 'Approved' : 'Rejected'}
              {request.reviewed_at && ` ${new Date(request.reviewed_at).toLocaleDateString()}`}
              {request.review_note && ` · ${request.review_note}`}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PendingRequest({ request }: { request: InvestigatorAccessRequest }) {
  const approve = useApproveAccessRequest();
  const reject = useRejectAccessRequest();
  const sameTenant = request.holders.filter(holder => holder.same_tenant);
  const elsewhere = request.holders.filter(holder => !holder.same_tenant);
  const [target, setTarget] = useState(
    request.suggested_team_id ?? (request.holders.length === 0 ? CREATE_TEAM : '')
  );
  const [note, setNote] = useState('');
  const busy = approve.isPending || reject.isPending;
  const piName = request.pi_name ?? request.pi_slug;
  const canApprove = target !== '';

  const onApprove = async () => {
    try {
      await approve.mutateAsync({
        id: request.id,
        body: {
          ...(target === CREATE_TEAM ? { create_team: true } : { team_id: target }),
          ...(note.trim() ? { review_note: note.trim() } : {}),
        },
      });
      toast.success(`Placed in ${target === CREATE_TEAM ? piName : 'the team'}`);
    } catch (error) {
      toast.error(errorText(error, 'Could not approve this request'));
    }
  };

  const onReject = async () => {
    try {
      await reject.mutateAsync({
        id: request.id,
        body: note.trim() ? { review_note: note.trim() } : {},
      });
      toast.success('Request rejected');
    } catch (error) {
      toast.error(errorText(error, 'Could not reject this request'));
    }
  };

  return (
    <article
      aria-label={`Access request for ${piName}`}
      className="space-y-3 rounded-3xl border p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{piName}</span>
        <Badge variant="secondary">{relationLabel(request)}</Badge>
        <Badge variant={request.email_matched ? 'default' : 'outline'}>
          {request.email_matched ? 'Email matches the catalog' : 'Email does not match the catalog'}
        </Badge>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Organisation</dt>
        <dd>{request.tenant_name ?? request.tenant_id}</dd>
        <dt className="text-muted-foreground">Current team</dt>
        <dd>
          {request.current_team ? (
            <Link
              to="/tenants/$tenantId/teams/$teamId"
              params={{ tenantId: request.tenant_id, teamId: request.current_team.id }}
              search={{ tab: 'members' }}
              className="underline underline-offset-4"
            >
              {request.current_team.name}
            </Link>
          ) : (
            'No team'
          )}
        </dd>
        <dt className="text-muted-foreground">Requested</dt>
        <dd>{new Date(request.created_at).toLocaleString()}</dd>
        {request.message && (
          <>
            <dt className="text-muted-foreground">Note</dt>
            <dd className="whitespace-pre-wrap">{request.message}</dd>
          </>
        )}
        <dt className="text-muted-foreground">Teams with this PI</dt>
        <dd>
          {request.holders.length ? (
            <ul>
              {request.holders.map(holder => (
                <li key={holder.team_id}>
                  <Link
                    to="/tenants/$tenantId/teams/$teamId"
                    params={{ tenantId: holder.tenant_id, teamId: holder.team_id }}
                    search={{ tab: 'members' }}
                    className="underline underline-offset-4"
                  >
                    {holder.team_name}
                  </Link>{' '}
                  <span className="text-muted-foreground">· {holder.tenant_name}</span>
                </li>
              ))}
            </ul>
          ) : (
            'No team holds this PI yet'
          )}
        </dd>
      </dl>
      {sameTenant.length === 0 && elsewhere.length > 0 && (
        <p role="note" className="text-sm text-muted-foreground">
          Only another organisation holds this PI. Move the person to that organisation first, or
          create a team for the PI here.
        </p>
      )}
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Place in
        <select
          aria-label="Place in"
          value={target}
          onChange={event => setTarget(event.target.value)}
          className="h-10 w-full rounded-3xl border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {sameTenant.length === 0 && elsewhere.length > 0 && (
            <option value="">Choose where to place them</option>
          )}
          {sameTenant.map(holder => (
            <option key={holder.team_id} value={holder.team_id}>
              {holder.team_name}
            </option>
          ))}
          {sameTenant.length === 0 && (
            <option value={CREATE_TEAM}>Create a team named after {piName}</option>
          )}
        </select>
      </label>
      <Textarea
        aria-label="Review note"
        placeholder="Optional note the member sees"
        value={note}
        maxLength={1000}
        rows={2}
        onChange={event => setNote(event.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy || !canApprove} onClick={() => void onApprove()}>
          Approve
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => void onReject()}>
          Reject
        </Button>
      </div>
    </article>
  );
}
